import { useEffect, useMemo, useState } from "react";
import {
  adminPreviewAudience,
  adminSendToAudience,
  getMetros,
  AdminAudience,
} from "../../../../api/apiEndpoints";
import { Chip, Chips, Note } from "../../../../components/PartnerDashboard/ui";
import {
  SectionTitle, Card, Row, Input, Select, Button, Textarea, Label, FormField, StatusText,
} from "../styles";

type Status = "active" | "cancelling" | "paused" | "lapsed" | "never";

const STATUSES: { key: Status; label: string }[] = [
  { key: "active", label: "Active" },
  { key: "cancelling", label: "Set to cancel" },
  { key: "paused", label: "Paused" },
  { key: "lapsed", label: "Lapsed (had a membership)" },
  { key: "never", label: "Never purchased" },
];

interface Metro {
  name: string;
  latitude: number;
  longitude: number;
  count: number;
}

interface Preview {
  matched: number;
  reachable: number;
  skippedByCap: number;
  unreachable: number;
  sample: { name: string }[];
}

const NotificationsPage = ({ allBusiness }: { allBusiness: any[] }) => {
  const [channel, setChannel] = useState<"email" | "push">("push");
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [metros, setMetros] = useState<Metro[]>([]);
  const [metroName, setMetroName] = useState("");
  const [radius, setRadius] = useState("25");
  const [gymName, setGymName] = useState("");
  const [productName, setProductName] = useState<"" | "basic" | "premium">("");
  const [abandoned, setAbandoned] = useState(false);
  const [abandonedDays, setAbandonedDays] = useState("7");
  const [abandonedGymId, setAbandonedGymId] = useState("");
  const [userIdsRaw, setUserIdsRaw] = useState("");
  const [capOn, setCapOn] = useState(true);
  const [capHours, setCapHours] = useState("24");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [status, setStatus] = useState<{ msg: string; ok: boolean } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getMetros()
      .then((res) => setMetros((res.data as any)?.metros ?? []))
      .catch(() => setMetros([]));
  }, []);

  const gyms = useMemo(
    () =>
      allBusiness
        .filter((b: any) => b?.name)
        .map((b: any) => ({ id: b.businessId as string, name: b.name as string }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allBusiness],
  );
  const gymNames = useMemo(() => Array.from(new Set(gyms.map((g) => g.name))), [gyms]);

  const audience = useMemo<AdminAudience>(() => {
    const a: AdminAudience = {};
    if (statuses.length) a.membership = statuses;
    const metro = metros.find((m) => m.name === metroName);
    const radiusMiles = Number(radius);
    if (metro && radiusMiles > 0) {
      a.near = { label: metro.name, latitude: metro.latitude, longitude: metro.longitude, radiusMiles };
    }
    if (gymName) a.gymName = gymName;
    if (productName) a.productName = productName;
    if (abandoned && Number(abandonedDays) > 0) {
      a.abandonedCart = { days: Math.round(Number(abandonedDays)), ...(abandonedGymId ? { gymId: abandonedGymId } : {}) };
    }
    const ids = userIdsRaw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (ids.length) a.userIds = ids;
    return a;
  }, [statuses, metros, metroName, radius, gymName, productName, abandoned, abandonedDays, abandonedGymId, userIdsRaw]);

  const capHoursNum = Math.max(0, Math.round(Number(capHours) || 0));
  const requestKey = JSON.stringify({ audience, channel, capHoursNum, capOn });

  useEffect(() => {
    let cancelled = false;
    setPreviewing(true);
    setPreviewError(null);
    const t = setTimeout(() => {
      adminPreviewAudience({ audience, channel, capHours: capHoursNum, overrideCap: !capOn })
        .then((res) => !cancelled && setPreview(res.data as Preview))
        .catch((e) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewError(e?.response?.data?.error || "Couldn't count this audience.");
        })
        .finally(() => !cancelled && setPreviewing(false));
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [requestKey]);

  const toggleStatus = (key: Status) =>
    setStatuses((prev) => (prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]));

  const noFilters = Object.keys(audience).length === 0;
  const channelWord = channel === "email" ? "email" : "push notification";
  const canSend = !!title.trim() && !!message.trim() && !!preview && preview.reachable > 0 && !loading;

  const send = async (test: boolean) => {
    if (!test) {
      const n = preview?.reachable ?? 0;
      const scope = noFilters ? "EVERY member" : `${n} ${n === 1 ? "person" : "people"}`;
      if (!window.confirm(`Send this ${channelWord} to ${scope}? This can't be undone.`)) return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const res = await adminSendToAudience({
        audience,
        channel,
        capHours: capHoursNum,
        overrideCap: !capOn,
        title: title.trim(),
        body: message.trim(),
        test,
      });
      const d = res.data as any;
      setStatus(
        test
          ? { msg: `Test ${channelWord} sent to you.`, ok: true }
          : {
              msg: `Sent to ${d.reached} of ${d.matched} matched${d.failed ? ` (${d.failed} failed)` : ""}${
                d.skippedByCap ? `, ${d.skippedByCap} skipped (messaged recently)` : ""
              }.`,
              ok: true,
            },
      );
    } catch (e: any) {
      setStatus({ msg: e?.response?.data?.error || e?.response?.data?.message || "Failed to send.", ok: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <SectionTitle>Send Notification</SectionTitle>

      <Row style={{ marginBottom: 20 }}>
        <Button variant={channel === "push" ? "primary" : "secondary"} onClick={() => setChannel("push")}>Push Notification</Button>
        <Button variant={channel === "email" ? "primary" : "secondary"} onClick={() => setChannel("email")}>Email</Button>
      </Row>

      <FormField style={{ marginBottom: 16 }}>
        <Label>Membership status</Label>
        <Chips>
          {STATUSES.map((s) => (
            <Chip key={s.key} $active={statuses.includes(s.key)} onClick={() => toggleStatus(s.key)}>
              {s.label}
            </Chip>
          ))}
        </Chips>
        <Note>Pick any to include people in those groups. Leave empty for everyone.</Note>
      </FormField>

      <FormField style={{ marginBottom: 16 }}>
        <Label>Location</Label>
        <Row>
          <Select value={metroName} onChange={(e) => setMetroName(e.target.value)} style={{ maxWidth: 280 }}>
            <option value="">Anywhere</option>
            {metros.map((m) => (
              <option key={m.name} value={m.name}>{m.name}</option>
            ))}
          </Select>
          {metroName && (
            <>
              <Input
                type="number"
                min={1}
                max={200}
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                style={{ maxWidth: 90 }}
                aria-label="Radius in miles"
              />
              <span style={{ alignSelf: "center", fontSize: 13, color: "#6b6259" }}>miles</span>
            </>
          )}
        </Row>
        <Note>Matches people whose last app location, or current gym, is within the radius.</Note>
      </FormField>

      <Row style={{ marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
        <FormField style={{ minWidth: 240, flex: 1 }}>
          <Label>Current gym</Label>
          <Select value={gymName} onChange={(e) => setGymName(e.target.value)}>
            <option value="">Any gym</option>
            {gymNames.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Select>
        </FormField>
        <FormField style={{ minWidth: 200 }}>
          <Label>Current plan</Label>
          <Select value={productName} onChange={(e) => setProductName(e.target.value as any)}>
            <option value="">Any plan</option>
            <option value="basic">Trial (basic)</option>
            <option value="premium">Upgrade (premium)</option>
          </Select>
        </FormField>
      </Row>

      <FormField style={{ marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input type="checkbox" checked={abandoned} onChange={(e) => setAbandoned(e.target.checked)} />
          Started checkout but didn't finish
        </label>
        {abandoned && (
          <Row style={{ marginTop: 8, gap: 8, flexWrap: "wrap" }}>
            <span style={{ alignSelf: "center", fontSize: 13, color: "#6b6259" }}>In the last</span>
            <Input type="number" min={1} max={90} value={abandonedDays} onChange={(e) => setAbandonedDays(e.target.value)} style={{ maxWidth: 80 }} />
            <span style={{ alignSelf: "center", fontSize: 13, color: "#6b6259" }}>days, at</span>
            <Select value={abandonedGymId} onChange={(e) => setAbandonedGymId(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">any gym</option>
              {gyms.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </Select>
          </Row>
        )}
      </FormField>

      <FormField style={{ marginBottom: 16 }}>
        <Label>Specific user IDs (optional)</Label>
        <Textarea placeholder="uuid1, uuid2…" value={userIdsRaw} onChange={(e) => setUserIdsRaw(e.target.value)} style={{ minHeight: 50 }} />
      </FormField>

      <FormField style={{ marginBottom: 20 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, flexWrap: "wrap" }}>
          <input type="checkbox" checked={capOn} onChange={(e) => setCapOn(e.target.checked)} />
          Skip people who got a {channel === "email" ? "marketing email" : "push"} in the last
          <Input
            type="number"
            min={1}
            max={336}
            value={capHours}
            disabled={!capOn}
            onChange={(e) => setCapHours(e.target.value)}
            style={{ maxWidth: 70 }}
          />
          hours
        </label>
        {!capOn && <Note>Cap is off. Only turn this off for urgent messages.</Note>}
      </FormField>

      <div
        style={{
          background: "#faf8f4",
          border: "1px solid #ece4da",
          borderRadius: 12,
          padding: "12px 14px",
          marginBottom: 20,
          fontSize: 14,
          color: "#111",
        }}
      >
        {previewError ? (
          <span style={{ color: "#b3261e" }}>{previewError}</span>
        ) : !preview || previewing ? (
          <span style={{ color: "#6b6259" }}>Counting audience…</span>
        ) : (
          <>
            <div style={{ fontWeight: 700 }}>
              {preview.reachable.toLocaleString()} {preview.reachable === 1 ? "person" : "people"} will receive this {channelWord}
            </div>
            <div style={{ color: "#6b6259", marginTop: 4 }}>
              {preview.matched.toLocaleString()} matched
              {preview.skippedByCap ? ` · ${preview.skippedByCap.toLocaleString()} skipped (messaged recently)` : ""}
              {preview.unreachable
                ? ` · ${preview.unreachable.toLocaleString()} can't be reached (${channel === "email" ? "opted out of email" : "push not enabled"})`
                : ""}
            </div>
            {preview.sample.length > 0 && (
              <div style={{ color: "#6b6259", marginTop: 4 }}>
                Including {preview.sample.map((s) => s.name).join(", ")}
                {preview.reachable > preview.sample.length ? "…" : ""}
              </div>
            )}
            {noFilters && <div style={{ color: "#b3261e", marginTop: 6 }}>No filters set: this goes to every member.</div>}
          </>
        )}
      </div>

      <FormField style={{ marginBottom: 16 }}>
        <Label>{channel === "email" ? "Subject" : "Push title"}</Label>
        <Input
          placeholder={channel === "email" ? "Email subject" : "Notification title"}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </FormField>

      <FormField style={{ marginBottom: 20 }}>
        <Label>Message</Label>
        <Textarea placeholder="Write your message here…" value={message} onChange={(e) => setMessage(e.target.value)} />
      </FormField>

      <Row style={{ gap: 10 }}>
        <Button variant="secondary" onClick={() => send(true)} disabled={!title.trim() || !message.trim() || loading}>
          Send test to me
        </Button>
        <Button onClick={() => send(false)} disabled={!canSend}>
          {loading ? "Sending…" : preview ? `Send to ${preview.reachable.toLocaleString()}` : "Send"}
        </Button>
      </Row>

      {status && <StatusText success={status.ok} style={{ marginTop: 12 }}>{status.msg}</StatusText>}
    </Card>
  );
};

export default NotificationsPage;
